import { useEffect, useRef } from "react";
import { useUser } from "@clerk/clerk-react";
import * as Sentry from "@sentry/react";
import { useRootStore } from "@/state/RootStore";
import { clearUserScopedStorage } from "@/utils/storage";
import { resolveIdentityTransition } from "@/state/identityOwnership";
import { clearClerkSession } from "@/lib/useAuthedFetch";

/** Which account last used this browser. Not user data — just the owner marker. */
const LAST_IDENTITY_KEY = "last_identity_user_id";

function readLastIdentity(): string | null {
  try {
    return localStorage.getItem(LAST_IDENTITY_KEY);
  } catch {
    return null;
  }
}

function writeLastIdentity(userId: string) {
  try {
    localStorage.setItem(LAST_IDENTITY_KEY, userId);
  } catch {
    /* storage unavailable — the owner stamp on the exam still guards the leak */
  }
}

/**
 * Clears state belonging to a previous account when a different one signs in.
 *
 * `useLogout` already sweeps storage, but it is only one of several ways to leave an
 * account: Clerk session expiry, a sign-out from Clerk's own UI, and a sign-out in
 * another tab all bypass it entirely. Those paths left an unfinished exam, a cached
 * profile, and a question-frequency map for the next account to adopt.
 *
 * Keying off the account that last used the browser catches every one of them, because
 * it checks the arriving identity rather than trusting a departing one to clean up.
 */
export function useIdentityTransition() {
  const store = useRootStore();
  const { user, isLoaded } = useUser();
  const appliedForRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isLoaded) return;
    const currentUserId = user?.id ?? null;
    if (!currentUserId) return;

    // Clerk re-renders this hook far more often than identity actually changes.
    if (appliedForRef.current === currentUserId) return;
    appliedForRef.current = currentUserId;
    clearClerkSession();

    const transition = resolveIdentityTransition(
      readLastIdentity(),
      currentUserId
    );

    if (transition === "new-user") {
      Sentry.captureMessage("identity_transition_cleared_previous_user", "info");
      // Storage first, then in-memory: clearing memory alone would let the stores
      // re-persist the previous account's values straight back to disk.
      clearUserScopedStorage();
      void store.reset();
    }

    writeLastIdentity(currentUserId);
    store.testStore.enforceActiveTestOwner(currentUserId);
  }, [isLoaded, user?.id, store]);
}
